/**
 * asad-transitions.js
 * Nahhar Fashions — Animated Page Transitions
 *
 * Strategy:
 * 1. Uses the native View Transitions API (Chrome 111+, Edge 111+) when available.
 *    This gives a true cross-fade that works with forward AND back/forward navigation.
 * 2. Falls back to a CSS opacity + translateY fade for Safari/Firefox.
 * 3. Never blocks navigation — if anything errors, we fall through to a normal page load.
 * 4. Respects prefers-reduced-motion.
 */
(function () {
  'use strict';

  const DURATION_MS = 380;
  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

  // Respect user motion preference
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    return; // Skip all transitions — accessibility first
  }

  // ── CSS fallback animation ─────────────────────────────
  // Injected once; used when View Transitions API isn't available.
  // Also used as the ::view-transition-* animation for browsers that support it.

  const styleEl = document.createElement('style');
  styleEl.id = 'asad-transition-styles';
  styleEl.textContent = `
    /* Page wrapper fade — CSS fallback */
    @keyframes nf-fade-in {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes nf-fade-out {
      from { opacity: 1; transform: translateY(0); }
      to   { opacity: 0; transform: translateY(-6px); }
    }

    /* View Transitions API animation overrides */
    @supports (view-transition-name: none) {
      ::view-transition-old(root) {
        animation: ${DURATION_MS}ms ${EASE} both nf-fade-out;
      }
      ::view-transition-new(root) {
        animation: ${DURATION_MS}ms ${EASE} both nf-fade-in;
      }
    }

    /* Fallback: applied when VTA isn't available */
    body.nf-page-entering {
      animation: nf-fade-in ${DURATION_MS}ms ${EASE} both;
    }
    body.nf-page-leaving {
      animation: nf-fade-out ${DURATION_MS / 2}ms ${EASE} both;
      pointer-events: none;
    }
  `;
  document.head.appendChild(styleEl);

  // ── Helpers ─────────────────────────────────────────────

  function isInternalLink(href) {
    if (!href) return false;
    try {
      const url = new URL(href, window.location.origin);
      // Same origin only
      if (url.origin !== window.location.origin) return false;
      // Skip hash-only links (smooth scroll, not page nav)
      if (url.pathname === window.location.pathname && url.hash) return false;
      // Skip Shopify checkout, cart actions, account, admin
      const skip = ['/checkout', '/cart/add', '/cart/change', '/cart/clear', '/account/login', '/account/logout', '/admin'];
      if (skip.some((s) => url.pathname.startsWith(s))) return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  function shouldSkipLink(el) {
    // Skip modified clicks (cmd+click, ctrl+click, middle click, download)
    return el.hasAttribute('download')
      || el.hasAttribute('target')
      || el.getAttribute('rel') === 'external';
  }

  // ── CSS fallback transition ──────────────────────────────

  function triggerFallbackEnter() {
    document.body.classList.remove('nf-page-leaving');
    document.body.classList.add('nf-page-entering');
    setTimeout(() => {
      document.body.classList.remove('nf-page-entering');
    }, DURATION_MS + 50);
  }

  function triggerFallbackLeave(callback) {
    document.body.classList.remove('nf-page-entering');
    document.body.classList.add('nf-page-leaving');
    setTimeout(callback, DURATION_MS / 2);
  }

  // ── View Transitions API path ────────────────────────────

  const supportsVTA = !!document.startViewTransition;

  function navigateWithTransition(href) {
    if (supportsVTA) {
      document.startViewTransition(() => {
        window.location.href = href;
      });
    } else {
      triggerFallbackLeave(() => {
        window.location.href = href;
      });
    }
  }

  // ── Intercept all internal link clicks ──────────────────

  document.addEventListener('click', function (e) {
    // Walk up the DOM to find an anchor
    let target = e.target;
    while (target && target.tagName !== 'A') {
      target = target.parentElement;
    }
    if (!target) return;

    const href = target.getAttribute('href');
    if (!href || !isInternalLink(href) || shouldSkipLink(target)) return;

    // Modified click — open in new tab etc.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    e.preventDefault();
    navigateWithTransition(href);
  }, { capture: true });

  // ── Back/Forward navigation ──────────────────────────────
  // When the browser loads a page from bfcache (back/forward), trigger enter animation.

  window.addEventListener('pageshow', function (e) {
    // e.persisted = true means it came from bfcache (back/forward)
    if (e.persisted) {
      // Ensure the page is visible (bfcache sometimes freezes it)
      document.body.style.opacity = '0';
      requestAnimationFrame(() => {
        document.body.style.transition = `opacity ${DURATION_MS}ms ${EASE}, transform ${DURATION_MS}ms ${EASE}`;
        document.body.style.transform = 'translateY(8px)';
        requestAnimationFrame(() => {
          document.body.style.opacity = '1';
          document.body.style.transform = 'translateY(0)';
          setTimeout(() => {
            document.body.style.transition = '';
            document.body.style.transform = '';
            document.body.style.opacity = '';
          }, DURATION_MS + 50);
        });
      });
    }
  });

  // ── Entry animation on fresh page load ──────────────────
  // Only for the CSS fallback path — VTA handles its own animation.

  if (!supportsVTA) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', triggerFallbackEnter);
    } else {
      triggerFallbackEnter();
    }
  }
})();
